import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { catchError, mergeMap, of, from, forkJoin, map, Observable } from "rxjs";
import filesService, { type SharedItemsResponse } from "@/pages/FilePage/services/files.service";
import { fileActions } from "@/store/file/fileSlice";
import { mapApiToFolderRecord, type FilePermissions } from "@/pages/FilePage/utils/fileUtils";
import type { FileRecord } from "@/common/define";
import { FolderService } from "@/services/FolderService";
import { getCompanyId } from "@/helper/company.helper";
import { UploadFileService } from "@/services/UploadFileService";
import { set } from "lodash";

interface TeamDrivesState {
  teamItems: FileRecord[];
  teamItemsTrash: FileRecord[];
  teamItemsLoading: boolean;
  teamItemsError?: string;
  currentFolderPermissions: FilePermissions | null;
  teamItemsPagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  teamDrivesStorage: {
    byId: Record<string, {
      teamId: string;
      name: string;
      storageSize: number;
      capacitySize: number;
    }>;
    allIds: string[];
  };
}

interface UpdateTeamDriveStoragePayload {
  teamId: string;      // labelId / folderRootId
  deltaSize: number;   // tổng size upload thành công (bytes)
}

const initialState: TeamDrivesState = {
  teamItems: [],
  teamItemsTrash: [],
  teamItemsLoading: false,
  currentFolderPermissions: null,
  teamItemsPagination: {
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  },
  teamDrivesStorage: {
    byId: {},
    allIds: [],
  },
};

const teamDrivesSlice = createSlice({
  name: "teamDrives",
  initialState,
  reducers: {
    // Team items actions
    getTeamItemsRequest: (state, _action: PayloadAction<{ page?: number; pageSize?: number }>) => {
      state.teamItemsLoading = true;
      state.teamItemsError = undefined;
    },
    // Team drives folder actions (for admin users)
    getTeamDrivesFolderRequest: (state, _action: PayloadAction<{ page?: number; pageSize?: number }>) => {
      state.teamItemsLoading = true;
      state.teamItemsError = undefined;
    },
    getTeamDrivesFolderSuccess: (state, action: PayloadAction<{ items: FileRecord[]; pagination: any }>) => {
      state.teamItemsLoading = false;
      state.teamItems = action.payload.items;
      state.teamItemsPagination = {
        page: action.payload.pagination.page,
        pageSize: action.payload.pagination.pageSize,
        totalCount: action.payload.pagination.queryCount,
        totalPages: action.payload.pagination.pageCount,
      };
      state.teamItemsTrash = action.payload.items;
    },
    getTeamDrivesFolderFailure: (state, action: PayloadAction<string>) => {
      state.teamItemsLoading = false;
      state.teamItemsError = action.payload;
    },
    getTeamItemsSuccess: (state, action: PayloadAction<{ items: FileRecord[]; pagination: any }>) => {
      state.teamItemsLoading = false;
      state.teamItems = action.payload.items;
      state.teamItemsPagination = {
        page: action.payload.pagination.page,
        pageSize: action.payload.pagination.pageSize,
        totalCount: action.payload.pagination.queryCount,
        totalPages: action.payload.pagination.pageCount,
      };
    },
    getTeamItemsFailure: (state, action: PayloadAction<string>) => {
      state.teamItemsLoading = false;
      state.teamItemsError = action.payload;
    },
    // Check team items availability without updating pagination
    checkTeamItemsAvailability: (state) => {
      state.teamItemsError = undefined;
    },
    checkTeamItemsSuccess: (state, action: PayloadAction<FileRecord[]>) => {
      state.teamItems = action.payload;
      state.teamItemsTrash = action.payload;
    },
    checkTeamItemsFailure: (state, action: PayloadAction<string>) => {
      state.teamItemsError = action.payload;
    },
    
    // Get Team Drives for Trash Selector (lightweight, no storage info)
    getTeamDrivesForTrashSelectorRequest: (state) => {
      state.teamItemsLoading = true;
      state.teamItemsError = undefined;
    },
    getTeamDrivesForTrashSelectorSuccess: (state, action: PayloadAction<FileRecord[]>) => {
      state.teamItemsLoading = false;
      state.teamItemsTrash = action.payload;
    },
    getTeamDrivesForTrashSelectorFailure: (state, action: PayloadAction<string>) => {
      state.teamItemsLoading = false;
      state.teamItemsError = action.payload;
    },
    
    setCurrentFolderPermissions: (state, action: PayloadAction<FilePermissions | null>) => {
      state.currentFolderPermissions = action.payload;
    },
    setTeamDrivesStorage: (state, action: PayloadAction<{
      byId: Record<string, {
        teamId: string;
        name: string;
        storageSize: number;
        capacitySize: number;
      }>;
      allIds: string[];
    }>) => {
      state.teamDrivesStorage = action.payload;
    },
    updateTeamDriveStorage(
      state,
      action: PayloadAction<UpdateTeamDriveStoragePayload>
    ) {
      const { teamId, deltaSize } = action.payload;

      const team = state.teamDrivesStorage.byId[teamId];
      if (!team) {
        return;
      }

       team.storageSize = Math.max(0, team.storageSize + deltaSize);
    },
    setTeamItemsLoading: (state, action: PayloadAction<boolean>) => {
      state.teamItemsLoading = action.payload;
    }
  },
});

export const teamDrivesActions = teamDrivesSlice.actions;
export default teamDrivesSlice.reducer;

// --- Epic ---
export const getTeamItemsEpic = (action$: any) =>
  action$.pipe(
    ofType(teamDrivesActions.getTeamItemsRequest.type),
    mergeMap((action: any) => {
      const { page = 1, pageSize = 20 } = action.payload || {};
      const companyId = getCompanyId();

      return filesService.getTeamItems(page, pageSize).pipe(
        mergeMap((response: SharedItemsResponse) => {
          // Filter out label items to fetch storage info
          const labelItems = response.results.filter(
            item => item.label && item.label.isRootOrganization === true
          );

          // Create storage requests for all labels
          const storageRequests = labelItems.map(item =>
            UploadFileService.Get.getCompanyStorageUsage(
              companyId,
              item.label!.id
            ).pipe(

              map(storageInfo => ({ labelId: item.label!.id, storageInfo })),
              catchError(err => {
                console.warn(`Failed to fetch storage for label ${item.label!.id}:`, err);
                return of({ labelId: item.label!.id, storageInfo: null });
              })
            )
          );

          // If there are no labels, skip the storage fetch
          const storageData$ = storageRequests.length > 0
            ? forkJoin(storageRequests)
            : of([]);

          return storageData$.pipe(
            mergeMap((storageResults: any[]) => {
              // Create a map of labelId to storage info
              const storageMap = new Map(
                storageResults.map(r => [r.labelId, r.storageInfo])
              );

              // Transform và map qua mapApiToFolderRecord
              const mappedItems = response.results.map((item) => {
                let transformedItem: any;

                if (item.document) {
                  const doc = item.document;
                  transformedItem = {
                    id: doc.id,
                    name: doc.name,
                    type: doc.type || 'file',
                    version: doc.version,
                    size: doc.size,
                    owner: doc.owner,
                    createUser: doc.owner,
                    lastModified: doc.lastModified,
                    bucketName: doc.bucketName || '',
                    cloudLink: doc.cloudLink || '',
                    fileId: doc.fileId,
                    fileCode: doc.fileCode,
                    jsonContent: doc.jsonContent,
                    isPublish: doc.isPublish,
                    parentId: doc.parentId,
                    createDate: doc.createDate,
                    fileType: doc.fileType,
                    permissions: item.permissions,
                    raw: item,
                    isOrganization: true
                  };
                } else if (item.label && item.label.isRootOrganization === true) {
                  const lbl = item.label;
                  const storageInfo = storageMap.get(lbl.id);

                  transformedItem = {
                    id: lbl.id,
                    name: lbl.name,
                    type: 'folder',
                    version: '-',
                    size: storageInfo?.storageSize || 0,
                    owner: lbl.createUser,
                    createUser: lbl.createUser,
                    lastModified: lbl.lastModified,
                    bucketName: '',
                    cloudLink: '',
                    color: lbl.color,
                    labelCode: lbl.labelCode,
                    createdDate: lbl.createdDate,
                    users: lbl.users,
                    clientId: lbl.clientId,
                    labelChildren: lbl.labelChildren,
                    documentChildren: lbl.documentChildren,
                    fileType: lbl.fileType,
                    permissions: item.permissions,
                    raw: item,
                    isOrganization: true,
                    isRootOrganization: lbl.isRootOrganization || false,
                    storageInfo: storageInfo // Include full storage info for potential future use
                  };
                }

                return transformedItem ? transformedItem : null;
              }).filter(Boolean) as FileRecord[];

              return from([
                teamDrivesActions.getTeamItemsSuccess({
                  items: mappedItems,
                  pagination: response
                }),
                // Sync pagination into global file state (reducer will compute derived fields)
                fileActions.setPagination(response),
                fileActions.setPagination({
                  page: response.page,
                  pageCount: response.pageCount,
                  pageSize: response.pageSize,
                  queryCount: response.queryCount,
                  firstRowIndex: (response.page - 1) * response.pageSize + 1,
                  lastRowIndex: Math.min(
                    response.page * response.pageSize,
                    response.queryCount
                  ),
                })
              ]);
            })
          );
        }),
        catchError((err) =>
          of(
            teamDrivesActions.getTeamItemsFailure(
              err.message ?? "Error loading team items"
            )
          )
        )
      );
    })
  );

export const checkTeamItemsAvailabilityEpic = (action$: any) =>
  action$.pipe(
    ofType(teamDrivesActions.checkTeamItemsAvailability.type),
    mergeMap(() => {
      // Only fetch first page with minimal data to check availability
      return filesService.getTeamItems().pipe(
        mergeMap((response: SharedItemsResponse) => {
          // Simple transformation without storage info for faster check
          const mappedItems = response.results.map((item) => {
            let transformedItem: any;

            if (item.document) {
              const doc = item.document;
              transformedItem = {
                id: doc.id,
                name: doc.name,
                type: doc.type || 'file',
                version: doc.version,
                size: doc.size,
                owner: doc.owner,
                createUser: doc.owner,
                lastModified: doc.lastModified,
                bucketName: doc.bucketName || '',
                cloudLink: doc.cloudLink || '',
                fileId: doc.fileId,
                fileCode: doc.fileCode,
                jsonContent: doc.jsonContent,
                isPublish: doc.isPublish,
                parentId: doc.parentId,
                createDate: doc.createDate,
                fileType: doc.fileType,
                permissions: item.permissions,
                raw: item,
                isOrganization: true
              };
            } else if (item.label) {
              const lbl = item.label;
              transformedItem = {
                id: lbl.id,
                name: lbl.name,
                type: 'folder',
                version: '1',
                size: 0,
                owner: lbl.createUser,
                createUser: lbl.createUser,
                lastModified: lbl.lastModified,
                bucketName: '',
                cloudLink: '',
                color: lbl.color,
                labelCode: lbl.labelCode,
                createdDate: lbl.createdDate,
                users: lbl.users,
                clientId: lbl.clientId,
                labelChildren: lbl.labelChildren,
                documentChildren: lbl.documentChildren,
                fileType: lbl.fileType,
                permissions: item.permissions,
                raw: item,
                isOrganization: true
              };
            }

            return transformedItem ? transformedItem : null;
          }).filter(Boolean) as FileRecord[];

          return from([
            teamDrivesActions.checkTeamItemsSuccess(mappedItems)
          ]);
        }),
        catchError((err) =>
          of(
            teamDrivesActions.checkTeamItemsFailure(
              err.message ?? "Error checking team items"
            )
          )
        )
      );
    })
  );

export const getTeamDrivesFolderEpic = (action$: any) =>
  action$.pipe(
    ofType(teamDrivesActions.getTeamDrivesFolderRequest.type),
    mergeMap((action: any) => {
      const { page = 1, pageSize = 20 } = action.payload || {};
      const companyId = getCompanyId();
      const companyRootId = localStorage.getItem("companyRootId");

      if (!companyRootId) {
        return of(
          teamDrivesActions.getTeamDrivesFolderFailure(
            "Company root ID not found"
          )
        );
      }

      return filesService
        .getTeamDrivesFolder(companyRootId, page, pageSize)
        .pipe(
          mergeMap((response: any) => {
            const folders = response.results ?? [];

            // build requests for storage usage
            const storageRequests: Observable<any>[] = folders.map((folder: any) =>
              UploadFileService.Get.getCompanyStorageUsage(companyId, folder.id).pipe(
                map((storageInfo) => ({
                  labelId: folder.id,
                  storageInfo,
                })),
                catchError(() =>
                  of({
                    labelId: folder.id,
                    storageInfo: null,
                  })
                )
              )
            );

            const storageData$: Observable<any[]> =
              storageRequests.length > 0
                ? forkJoin<any[]>(storageRequests)
                : of([] as any[]);

            return storageData$.pipe(
              map((storageResults: any[]) => {
                const storageMap = new Map(
                  storageResults.map((r) => [r.labelId, r.storageInfo])
                );

                const mappedItems = folders
                  .map((f: any) => {
                    const storageInfo = storageMap.get(f.id);

                    const transformed = {
                      id: f.id,
                      name: f.name,
                      type: "folder",
                      version: "-",
                      size: storageInfo?.storageSize ?? 0,
                      owner: f.createUser,
                      createUser: f.createUser,
                      lastModified: f.lastModified,
                      bucketName: "",
                      cloudLink: "",
                      color: f.color,
                      labelCode: f.labelCode,
                      createdDate: f.createdDate,
                      users: f.users,
                      clientId: f.clientId,
                      fileType: "folder",
                      parentId: f.parentId,
                      permissions: [],
                      raw: f,
                      isOrganization: true,
                      isRootOrganization: f.isRootOrganization || false,
                      storageInfo,
                    };

                    return transformed;
                  })
                  .filter(Boolean);

                // Build normalized TeamDrivesStorage
                const teamDrivesStorageById: Record<string, {
                  teamId: string;
                  name: string;
                  storageSize: number;
                  capacitySize: number;
                }> = {};
                const teamDrivesStorageAllIds: string[] = [];

                storageResults.forEach((result) => {
                  if (result.storageInfo) {
                    const folder = folders.find((f: any) => f.id === result.labelId);
                    if (folder) {
                      teamDrivesStorageById[result.labelId] = {
                        teamId: result.labelId,
                        name: folder.name,
                        storageSize: result.storageInfo.storageSize,
                        capacitySize: result.storageInfo.capacitySize,
                      };
                      teamDrivesStorageAllIds.push(result.labelId);
                    }
                  }
                });

                return [
                  teamDrivesActions.getTeamDrivesFolderSuccess({
                    items: mappedItems,
                    pagination: response,
                  }),
                  teamDrivesActions.setTeamDrivesStorage({
                    byId: teamDrivesStorageById,
                    allIds: teamDrivesStorageAllIds,
                  }),
                  fileActions.setPagination({
                    page: response.page,
                    pageCount: response.pageCount,
                    pageSize: response.pageSize,
                    queryCount: response.queryCount,
                    firstRowIndex: (response.page - 1) * response.pageSize + 1,
                    lastRowIndex: Math.min(
                      response.page * response.pageSize,
                      response.queryCount
                    ),
                  })
                ];
              }),
              mergeMap((actions) => from(actions))
            );
          }),

          catchError((err) =>
            of(
              teamDrivesActions.getTeamDrivesFolderFailure(
                err?.message ?? "Error loading team drives folder"
              )
            )
          )
        );
    })
  );

// Epic for Trash Selector - lightweight without storage info
export const getTeamDrivesForTrashSelectorEpic = (action$: any) =>
  action$.pipe(
    ofType(teamDrivesActions.getTeamDrivesForTrashSelectorRequest.type),
    mergeMap(() => {
      const companyRootId = localStorage.getItem("companyRootId");

      if (!companyRootId) {
        return of(
          teamDrivesActions.getTeamDrivesForTrashSelectorFailure(
            "Company root ID not found"
          )
        );
      }

      // Fetch all folders without pagination for selector
      return filesService
        .getTeamDrivesFolder(companyRootId, 1, 99999999)
        .pipe(
          map((response: any) => {
            const folders = response.results ?? [];

            const mappedItems = folders
              .map((f: any) => {
                const transformedItem = {
                  id: f.id,
                  name: f.name,
                  type: 'folder',
                  format: 'FOLDER',
                  version: '1',
                  size: 0, // No storage info
                  owner: f.createUser,
                  createUser: f.createUser,
                  lastModified: f.lastModified,
                  color: f.color,
                  labelCode: f.labelCode,
                  createdDate: f.createdDate,
                  permissions: f.permissions,
                  isOrganization: true,
                };

                return transformedItem;
              })
              .filter(Boolean);

            return teamDrivesActions.getTeamDrivesForTrashSelectorSuccess(mappedItems);
          }),
          catchError((err) =>
            of(
              teamDrivesActions.getTeamDrivesForTrashSelectorFailure(
                err?.message ?? "Error loading team drives for trash selector"
              )
            )
          )
        );
    })
  );

export const teamDrivesEpic = [
  getTeamItemsEpic, 
  checkTeamItemsAvailabilityEpic, 
  getTeamDrivesFolderEpic,
  getTeamDrivesForTrashSelectorEpic,
];